import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Headphones, Play, Volume2, ArrowRight, CheckCircle, XCircle,
  RefreshCw, Copy, Users, Activity, Ear, AlertCircle, Clock,
  Download, Sparkles, SkipForward, Gauge, Bot, Hand, Pause,
  SquareIcon, Terminal, Zap, FileText
} from "lucide-react";
import { toast } from "sonner";

const FREQUENCIES = [250, 500, 1000, 2000, 4000, 8000];
const FREQUENCY_ORDER = [1000, 2000, 4000, 8000, 500, 250];
const MIN_DB = -10;
const MAX_DB = 120;
const START_DB = 40;

const fmtFreq = (f) => (f >= 1000 ? `${f / 1000}k` : f);

export default function PTAAudiologistPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mode
  const [testMode, setTestMode] = useState(null); // "manual" | "ai" | null

  // Manual test state
  const [currentEar, setCurrentEar] = useState("right");
  const [currentFrequency, setCurrentFrequency] = useState(FREQUENCY_ORDER[0]);
  const [currentDecibel, setCurrentDecibel] = useState(START_DB);
  const [freqIndex, setFreqIndex] = useState(0);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [lastToneId, setLastToneId] = useState(null);
  const [toneResult, setToneResult] = useState(null);

  // Thresholds & AI
  const [thresholds, setThresholds] = useState({ left: {}, right: {} });
  const [aiLog, setAiLog] = useState([]);
  const [aiActive, setAiActive] = useState(false);
  const [aiPaused, setAiPaused] = useState(false);
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [suggestedAids, setSuggestedAids] = useState([]);
  const [generatingRec, setGeneratingRec] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const pollRef = useRef(null);
  const timeoutRef = useRef(null);
  const logEndRef = useRef(null);

  // ─── Polling ───
  useEffect(() => {
    if (sessionId) {
      fetchSession();
      pollRef.current = setInterval(fetchSession, 1500);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [sessionId]);

  // Auto-scroll AI log
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [aiLog]);

  const fetchSession = async () => {
    try {
      const res = await apiClient.get(`/pta-live-sessions/${sessionId}`);
      const s = res.data;
      setSession(s);
      if (s.thresholds) setThresholds(s.thresholds);
      if (s.current_ear && (s.status === "testing_right" || s.status === "testing_left")) setCurrentEar(s.current_ear);
      if (s.ai_recommendation) setAiRecommendation(s.ai_recommendation);
      if (s.ai_test_active !== undefined) setAiActive(s.ai_test_active);
      if (s.ai_test_paused !== undefined) setAiPaused(s.ai_test_paused);
      if (s.ai_test_log) setAiLog(s.ai_test_log);
      if (s.ai_test_active) setTestMode("ai");

      // Check if patient responded to current manual tone
      if (waitingForResponse && lastToneId && s.tone_responses) {
        const tone = s.tone_responses.find((t) => t.tone_id === lastToneId);
        if (tone?.response_received) {
          clearTimeout(timeoutRef.current);
          setWaitingForResponse(false);
          setToneResult("heard");
          toast.success("Patient heard the tone!", { duration: 2000 });
        }
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ─── Phase Controls ───
  const startInstructions = async () => {
    try { await apiClient.post(`/pta-live-sessions/${sessionId}/start-instructions`); toast.success("Instructions shown to patient"); fetchSession(); } catch (e) { toast.error("Failed"); }
  };
  const startCalibration = async () => {
    try { await apiClient.post(`/pta-live-sessions/${sessionId}/start-calibration`); toast.success("Calibration started"); fetchSession(); } catch (e) { toast.error("Failed"); }
  };
  const startManualTest = async (ear) => {
    try {
      await apiClient.post(`/pta-live-sessions/${sessionId}/start-test?ear=${ear}`);
      setTestMode("manual"); setCurrentEar(ear); setCurrentFrequency(FREQUENCY_ORDER[0]); setCurrentDecibel(START_DB); setFreqIndex(0); setToneResult(null);
      toast.success(`Manual test — ${ear} ear`); fetchSession();
    } catch (e) { toast.error("Failed"); }
  };
  const startAITest = async () => {
    try {
      await apiClient.post(`/pta-live-sessions/${sessionId}/start-ai-test?ear=right`);
      setTestMode("ai"); setAiActive(true); toast.success("AI Automated Test started!"); fetchSession();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start AI test"); }
  };
  const pauseAI = async () => {
    try { await apiClient.post(`/pta-live-sessions/${sessionId}/pause-ai-test`); setAiPaused(true); toast.info("AI test paused"); } catch (e) { toast.error("Failed"); }
  };
  const resumeAI = async () => {
    try { await apiClient.post(`/pta-live-sessions/${sessionId}/resume-ai-test`); setAiPaused(false); toast.success("AI test resumed"); } catch (e) { toast.error("Failed"); }
  };
  const stopAI = async () => {
    try { await apiClient.post(`/pta-live-sessions/${sessionId}/stop-ai-test`); setTestMode("manual"); setAiActive(false); toast.info("Switched to manual mode"); fetchSession(); } catch (e) { toast.error("Failed"); }
  };

  // ─── Manual tone controls ───
  const presentTone = async () => {
    if (waitingForResponse) return;
    setToneResult(null); setWaitingForResponse(true);
    try {
      const res = await apiClient.post(`/pta-live-sessions/${sessionId}/present-tone?frequency=${currentFrequency}&decibel=${currentDecibel}&ear=${currentEar}`);
      setLastToneId(res.data.tone_id);
      timeoutRef.current = setTimeout(() => { setWaitingForResponse(false); setToneResult("no_response"); }, 5000);
    } catch (err) { toast.error("Failed to present tone"); setWaitingForResponse(false); }
  };
  const setThresholdManual = async () => {
    try {
      await apiClient.post(`/pta-live-sessions/${sessionId}/set-threshold?ear=${currentEar}&frequency=${currentFrequency}&threshold_db=${currentDecibel}`);
      setThresholds((p) => ({ ...p, [currentEar]: { ...p[currentEar], [currentFrequency]: currentDecibel } }));
      toast.success(`Threshold: ${fmtFreq(currentFrequency)}Hz = ${currentDecibel} dB`);
    } catch (e) { toast.error("Failed"); }
  };
  const goToNextFrequency = () => {
    const next = freqIndex + 1;
    if (next < FREQUENCY_ORDER.length) { setFreqIndex(next); setCurrentFrequency(FREQUENCY_ORDER[next]); setCurrentDecibel(START_DB); setToneResult(null); }
    else toast.info("All frequencies tested for this ear");
  };
  const selectFrequency = (freq) => {
    const idx = FREQUENCY_ORDER.indexOf(freq);
    if (idx >= 0) { setFreqIndex(idx); setCurrentFrequency(freq); setToneResult(null); }
    else { setCurrentFrequency(freq); setToneResult(null); }
  };
  const adjustDb = (delta) => { setCurrentDecibel((d) => Math.max(MIN_DB, Math.min(MAX_DB, d + delta))); setToneResult(null); };

  const completeTest = async () => {
    try { await apiClient.post(`/pta-live-sessions/${sessionId}/complete`); toast.success("Test completed!"); fetchSession(); } catch (e) { toast.error("Failed"); }
  };
  const generateAI = async () => {
    setGeneratingRec(true);
    try { const res = await apiClient.post(`/pta-live-sessions/${sessionId}/ai-recommendation`); setAiRecommendation(res.data.ai_recommendation); setSuggestedAids(res.data.hearing_aid_suggestions || []); toast.success("AI recommendation generated!"); }
    catch (e) { toast.error("Failed"); } finally { setGeneratingRec(false); }
  };
  const downloadReport = async () => {
    setDownloadingPdf(true);
    try {
      const res = await apiClient.get(`/pta-live-sessions/${sessionId}/report`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.setAttribute("download", `PTA_Report_${session?.patient_name?.replace(/\s/g, "_") || "Patient"}_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Report downloaded!");
    } catch (e) { toast.error("Failed to download"); } finally { setDownloadingPdf(false); }
  };

  const copyCode = () => { navigator.clipboard.writeText(session?.session_code || ""); toast.success("Code copied!"); };
  const copyUrl = () => { navigator.clipboard.writeText(`${window.location.origin}/pta-test/patient/${session?.session_code}`); toast.success("URL copied!"); };

  const statusMap = {
    created: { label: "Waiting for Patient", color: "bg-amber-100 text-amber-700", icon: Users },
    patient_joined: { label: "Patient Connected", color: "bg-green-100 text-green-700", icon: CheckCircle },
    instructions: { label: "Instructions", color: "bg-blue-100 text-blue-700", icon: Activity },
    calibration: { label: "Calibrating", color: "bg-purple-100 text-purple-700", icon: Headphones },
    testing_right: { label: "Testing Right Ear", color: "bg-red-100 text-red-700", icon: Ear },
    testing_left: { label: "Testing Left Ear", color: "bg-blue-100 text-blue-700", icon: Ear },
    completed: { label: "Completed", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  };
  const si = statusMap[session?.status] || { label: session?.status, color: "bg-gray-100 text-gray-700", icon: AlertCircle };
  const StatusIcon = si.icon;
  const isTesting = session?.status === "testing_right" || session?.status === "testing_left";
  const rightCount = Object.keys(thresholds.right || {}).length;
  const leftCount = Object.keys(thresholds.left || {}).length;
  const severityColor = (s) => ({ Normal: "text-emerald-600", Mild: "text-amber-500", Moderate: "text-orange-500", "Moderately Severe": "text-orange-600", Severe: "text-red-500", Profound: "text-red-700" }[s] || "text-gray-600");

  if (loading) return <DashboardLayout><div className="flex items-center justify-center min-h-[60vh]"><RefreshCw className="w-8 h-8 animate-spin text-teal-600" /></div></DashboardLayout>;
  if (!session) return <DashboardLayout><div className="p-8 text-center"><AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" /><h2 className="text-xl font-semibold text-gray-700">Session Not Found</h2></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="p-4 lg:p-6 space-y-4" data-testid="pta-audiologist-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Headphones className="w-6 h-6 text-teal-600" />
              Live PTA Console
              {aiActive && <Badge className="bg-gradient-to-r from-violet-500 to-indigo-600 text-white text-[10px] ml-2"><Bot className="w-3 h-3 mr-1" />AI MODE</Badge>}
              {testMode === "manual" && isTesting && <Badge className="bg-amber-100 text-amber-700 text-[10px] ml-2"><Hand className="w-3 h-3 mr-1" />MANUAL</Badge>}
            </h1>
            <p className="text-gray-500 text-sm">Patient: <strong>{session.patient_name}</strong></p>
          </div>
          <Badge className={si.color + " text-sm px-4 py-1.5"} data-testid="session-status"><StatusIcon className="w-4 h-4 mr-1.5" />{si.label}</Badge>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Left: Session Info */}
          <Card className="lg:col-span-1">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Session Info</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="bg-teal-50 rounded-xl p-3 text-center">
                <p className="text-[10px] text-teal-600 uppercase tracking-wider font-semibold">Session Code</p>
                <p className="text-2xl font-mono font-bold text-teal-700 tracking-wider">{session.session_code}</p>
                <div className="flex gap-1 mt-2 justify-center">
                  <Button variant="ghost" size="sm" onClick={copyCode} className="h-7 text-xs"><Copy className="w-3 h-3 mr-1" />Code</Button>
                  <Button variant="ghost" size="sm" onClick={copyUrl} className="h-7 text-xs"><Copy className="w-3 h-3 mr-1" />URL</Button>
                </div>
              </div>
              <div className="text-xs space-y-1.5 text-gray-600">
                <div className="flex justify-between"><span>Patient:</span><span className="font-medium text-gray-900">{session.patient_name}</span></div>
                <div className="flex justify-between"><span>Phone:</span><span className="font-medium">{session.patient_phone || "—"}</span></div>
                <div className="flex justify-between"><span>Headphones:</span><span className="font-medium">{session.headphone_type || "Generic"}</span></div>
              </div>
              <div className="border-t pt-2">
                <p className="text-xs font-semibold text-gray-700 mb-1">Thresholds Set</p>
                <div className="text-xs space-y-0.5">
                  <div className="flex justify-between"><span className="text-red-600">Right:</span><span>{rightCount}/{FREQUENCIES.length}</span></div>
                  <div className="flex justify-between"><span className="text-blue-600">Left:</span><span>{leftCount}/{FREQUENCIES.length}</span></div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Center: Controls */}
          <Card className="lg:col-span-3">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-teal-600" />Test Controls</CardTitle>
            </CardHeader>
            <CardContent>
              {/* PRE-TEST PHASES */}
              {session.status === "created" && (
                <div className="text-center py-10">
                  <Users className="w-14 h-14 text-amber-400 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Waiting for Patient</h3>
                  <p className="text-4xl font-mono font-bold text-teal-600 tracking-widest mb-2">{session.session_code}</p>
                  <code className="text-xs text-gray-400 block">{window.location.origin}/pta-test/patient/{session.session_code}</code>
                </div>
              )}
              {session.status === "patient_joined" && (
                <div className="text-center py-10">
                  <CheckCircle className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-gray-900 mb-6">Patient Connected!</h3>
                  <Button onClick={startInstructions} className="bg-teal-600 hover:bg-teal-700 px-8" data-testid="start-instructions-btn">
                    <Play className="w-4 h-4 mr-2" /> Show Instructions
                  </Button>
                </div>
              )}
              {session.status === "instructions" && (
                <div className="text-center py-10">
                  <Activity className="w-14 h-14 text-blue-500 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-gray-900 mb-6">Patient Reading Instructions</h3>
                  <Button onClick={startCalibration} className="bg-teal-600 hover:bg-teal-700 px-8" data-testid="start-calibration-btn">
                    <Headphones className="w-4 h-4 mr-2" /> Start Calibration
                  </Button>
                </div>
              )}

              {/* CALIBRATION — MODE SELECTION */}
              {session.status === "calibration" && (
                <div className="py-8">
                  <Volume2 className="w-14 h-14 text-purple-500 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-gray-900 text-center mb-2">Patient Calibrating Headphones</h3>
                  <p className="text-gray-500 text-center text-sm mb-8">When ready, choose your testing mode:</p>
                  <div className="grid sm:grid-cols-2 gap-4 max-w-xl mx-auto">
                    {/* AI Mode */}
                    <button onClick={startAITest} data-testid="start-ai-test-btn"
                      className="group relative p-6 bg-gradient-to-br from-violet-50 to-indigo-50 border-2 border-violet-200 rounded-2xl hover:border-violet-400 hover:shadow-lg transition-all text-left">
                      <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                        <Bot className="w-6 h-6 text-white" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-base">AI Automated Test</h4>
                      <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                        AI runs the full clinical audiometric protocol automatically. Real-time audiogram, instant PDF report, AI recommendations, and WhatsApp notification.
                      </p>
                      <Badge className="mt-3 bg-violet-100 text-violet-700 text-[10px]">Recommended</Badge>
                    </button>
                    {/* Manual Mode */}
                    <button onClick={() => startManualTest("right")} data-testid="start-manual-test-btn"
                      className="group relative p-6 bg-gray-50 border-2 border-gray-200 rounded-2xl hover:border-teal-300 hover:shadow-lg transition-all text-left">
                      <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                        <Hand className="w-6 h-6 text-white" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-base">Manual Test</h4>
                      <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                        Full manual control. You select frequencies, adjust dB levels, and set thresholds at your own pace.
                      </p>
                      <Badge className="mt-3 bg-gray-100 text-gray-600 text-[10px]">Full Control</Badge>
                    </button>
                  </div>
                </div>
              )}

              {/* ═══ AI TEST MODE ═══ */}
              {isTesting && testMode === "ai" && (
                <div className="space-y-4">
                  {/* AI Status Bar */}
                  <div className={`flex items-center justify-between p-3 rounded-xl border-2 ${
                    aiPaused ? "bg-amber-50 border-amber-200" : aiActive ? "bg-violet-50 border-violet-200" : "bg-emerald-50 border-emerald-200"
                  }`}>
                    <div className="flex items-center gap-2">
                      {aiActive && !aiPaused && <div className="w-2.5 h-2.5 bg-violet-500 rounded-full animate-pulse" />}
                      {aiPaused && <Pause className="w-4 h-4 text-amber-600" />}
                      <span className="text-sm font-semibold">
                        {aiPaused ? "AI Test Paused" : aiActive ? "AI Automated Audiometric Protocol Running" : "AI Test Complete"}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      {aiActive && !aiPaused && (
                        <Button size="sm" variant="outline" onClick={pauseAI} className="h-8 text-xs border-amber-400 text-amber-700"><Pause className="w-3 h-3 mr-1" />Pause</Button>
                      )}
                      {aiPaused && (
                        <Button size="sm" onClick={resumeAI} className="h-8 text-xs bg-violet-600 hover:bg-violet-700 text-white"><Play className="w-3 h-3 mr-1" />Resume</Button>
                      )}
                      {aiActive && (
                        <Button size="sm" variant="outline" onClick={stopAI} className="h-8 text-xs border-red-300 text-red-600"><SquareIcon className="w-3 h-3 mr-1" />Stop & Go Manual</Button>
                      )}
                    </div>
                  </div>

                  {/* Current state */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className={`p-3 rounded-xl text-center border-2 ${currentEar === "right" ? "bg-red-50 border-red-200" : "bg-blue-50 border-blue-200"}`}>
                      <Ear className={`w-5 h-5 mx-auto mb-1 ${currentEar === "right" ? "text-red-500" : "text-blue-500 scale-x-[-1]"}`} />
                      <p className="text-xs font-bold uppercase">{session.current_ear || currentEar} Ear</p>
                    </div>
                    <div className="p-3 rounded-xl text-center bg-gray-50 border border-gray-200">
                      <p className="text-xl font-bold text-gray-900">{session.current_frequency ? fmtFreq(session.current_frequency) : "—"}</p>
                      <p className="text-xs text-gray-500">Hz</p>
                    </div>
                    <div className="p-3 rounded-xl text-center bg-gray-50 border border-gray-200">
                      <p className="text-xl font-bold text-gray-900">{session.current_decibel ?? "—"}</p>
                      <p className="text-xs text-gray-500">dB HL</p>
                    </div>
                  </div>

                  {/* AI Live Log */}
                  <div className="bg-gray-900 rounded-xl p-4 max-h-48 overflow-y-auto font-mono text-xs" data-testid="ai-test-log">
                    <div className="flex items-center gap-2 mb-2 text-gray-400">
                      <Terminal className="w-3.5 h-3.5" /><span className="font-semibold">AI Test Log</span>
                    </div>
                    {aiLog.length === 0 && <p className="text-gray-500">Waiting for AI test to start...</p>}
                    {aiLog.map((entry, i) => (
                      <div key={i} className={`py-0.5 ${entry.level === "error" ? "text-red-400" : entry.level === "warn" ? "text-amber-400" : "text-green-400"}`}>
                        <span className="text-gray-600 mr-2">{entry.ts?.slice(11, 19) || ""}</span>
                        {entry.msg}
                      </div>
                    ))}
                    <div ref={logEndRef} />
                  </div>

                  {/* Frequency progress */}
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-2">Frequency Progress</p>
                    <div className="flex flex-wrap gap-2">
                      {FREQUENCY_ORDER.map((freq) => {
                        const rightDone = thresholds.right?.[freq] !== undefined;
                        const leftDone = thresholds.left?.[freq] !== undefined;
                        const isCurrent = session.current_frequency === freq;
                        return (
                          <div key={freq} className={`relative px-3 py-2 text-xs font-semibold rounded-xl border-2 ${
                            isCurrent ? "bg-violet-100 border-violet-400 text-violet-700 ring-2 ring-violet-300" :
                            rightDone && leftDone ? "bg-emerald-50 border-emerald-300 text-emerald-700" :
                            rightDone || leftDone ? "bg-amber-50 border-amber-200 text-amber-600" :
                            "bg-gray-50 border-gray-200 text-gray-400"
                          }`}>
                            {fmtFreq(freq)}Hz
                            <div className="flex gap-0.5 mt-1 justify-center">
                              <span className={`w-2 h-2 rounded-full ${rightDone ? "bg-red-500" : "bg-gray-200"}`} />
                              <span className={`w-2 h-2 rounded-full ${leftDone ? "bg-blue-500" : "bg-gray-200"}`} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* ═══ MANUAL TEST MODE ═══ */}
              {isTesting && testMode === "manual" && (
                <div className="space-y-5">
                  <div className="grid grid-cols-4 gap-3">
                    <div className={`p-3 rounded-xl text-center border-2 ${currentEar === "right" ? "bg-red-50 border-red-300" : "bg-blue-50 border-blue-300"}`}>
                      <Ear className={`w-6 h-6 mx-auto mb-1 ${currentEar === "right" ? "text-red-500" : "text-blue-500 scale-x-[-1]"}`} />
                      <p className="text-xs font-bold uppercase">{currentEar} Ear</p>
                    </div>
                    <div className="p-3 rounded-xl text-center bg-gray-50 border border-gray-200">
                      <p className="text-2xl font-bold text-gray-900">{fmtFreq(currentFrequency)}</p><p className="text-xs text-gray-500">Hz</p>
                    </div>
                    <div className="p-3 rounded-xl text-center bg-gray-50 border border-gray-200">
                      <p className="text-2xl font-bold text-gray-900">{currentDecibel}</p><p className="text-xs text-gray-500">dB HL</p>
                    </div>
                    <div className={`p-3 rounded-xl text-center border ${toneResult === "heard" ? "bg-emerald-50 border-emerald-300" : toneResult === "no_response" ? "bg-orange-50 border-orange-300" : waitingForResponse ? "bg-amber-50 border-amber-300" : "bg-gray-50 border-gray-200"}`}>
                      {waitingForResponse ? <><RefreshCw className="w-5 h-5 animate-spin mx-auto mb-1 text-amber-500" /><p className="text-xs font-bold text-amber-600">Waiting</p></>
                      : toneResult === "heard" ? <><CheckCircle className="w-5 h-5 mx-auto mb-1 text-emerald-500" /><p className="text-xs font-bold text-emerald-600">Heard</p></>
                      : toneResult === "no_response" ? <><XCircle className="w-5 h-5 mx-auto mb-1 text-orange-500" /><p className="text-xs font-bold text-orange-600">No Response</p></>
                      : <><Gauge className="w-5 h-5 mx-auto mb-1 text-gray-400" /><p className="text-xs text-gray-500">Ready</p></>}
                    </div>
                  </div>

                  {/* dB slider */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-600">Intensity Level</span>
                      <span className="text-sm font-bold text-gray-900">{currentDecibel} dB HL</span>
                    </div>
                    <input type="range" min={MIN_DB} max={MAX_DB} step={5} value={currentDecibel}
                      onChange={(e) => { setCurrentDecibel(parseInt(e.target.value)); setToneResult(null); }}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600" data-testid="db-slider" />
                    <div className="flex justify-center gap-2 mt-3">
                      {[-20, -10, -5, 5, 10, 20].map((d) => (
                        <button key={d} onClick={() => adjustDb(d)} className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all ${d < 0 ? "bg-white text-blue-600 border-blue-200 hover:bg-blue-50" : "bg-white text-red-600 border-red-200 hover:bg-red-50"}`}>
                          {d > 0 ? `+${d}` : d}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Frequency selector */}
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-2">Frequency (Hz)</p>
                    <div className="flex flex-wrap gap-2">
                      {FREQUENCY_ORDER.map((freq) => {
                        const has = thresholds[currentEar]?.[freq] !== undefined;
                        const active = currentFrequency === freq;
                        return (
                          <button key={freq} onClick={() => selectFrequency(freq)} data-testid={`freq-btn-${freq}`}
                            className={`relative px-4 py-2 text-sm font-semibold rounded-xl border-2 transition-all ${active ? "bg-teal-600 text-white border-teal-600 shadow-md" : has ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-white text-gray-600 border-gray-200 hover:border-teal-300"}`}>
                            {fmtFreq(freq)}Hz
                            {has && <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center"><CheckCircle className="w-3 h-3 text-white" /></span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-3 items-center justify-center pt-2 border-t">
                    <Button onClick={presentTone} disabled={waitingForResponse} size="lg" data-testid="present-tone-btn"
                      className="bg-emerald-600 hover:bg-emerald-700 px-8 py-3 text-base shadow-lg shadow-emerald-200 disabled:opacity-50">
                      {waitingForResponse ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Waiting...</> : <><Volume2 className="w-5 h-5 mr-2" />Present Tone</>}
                    </Button>
                    <Button onClick={setThresholdManual} variant="outline" data-testid="set-threshold-btn" className="border-amber-400 text-amber-700 hover:bg-amber-50 px-6">
                      <CheckCircle className="w-4 h-4 mr-2" /> Set Threshold ({currentDecibel} dB)
                    </Button>
                    <Button onClick={goToNextFrequency} variant="outline" data-testid="next-freq-btn" className="px-4">
                      Next Freq <SkipForward className="w-4 h-4 ml-1" />
                    </Button>
                  </div>

                  <div className="flex justify-between items-center pt-3 border-t">
                    <Button onClick={() => startManualTest(currentEar === "right" ? "left" : "right")} variant="outline" size="sm"
                      className={currentEar === "right" ? "border-blue-400 text-blue-600" : "border-red-400 text-red-600"}>
                      <ArrowRight className="w-3 h-3 mr-1" /> Switch to {currentEar === "right" ? "Left" : "Right"} Ear
                    </Button>
                    <Button onClick={completeTest} variant="outline" size="sm" className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                      disabled={rightCount + leftCount < 4} data-testid="complete-test-btn">
                      <CheckCircle className="w-3 h-3 mr-1" /> Complete Test
                    </Button>
                  </div>
                </div>
              )}

              {/* ═══ COMPLETED ═══ */}
              {session.status === "completed" && (
                <div className="space-y-5 py-4">
                  <div className="text-center">
                    <CheckCircle className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
                    <h3 className="text-xl font-bold text-gray-900">Test Complete!</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto">
                    <div className="p-4 bg-red-50 rounded-xl text-center border border-red-100">
                      <p className="text-xs text-red-600 font-semibold">Right Ear PTA</p>
                      <p className="text-3xl font-bold text-red-700">{session.pta_right ?? "—"}<span className="text-sm ml-1">dB</span></p>
                      <p className={`text-sm font-semibold ${severityColor(session.severity_right)}`}>{session.severity_right || "—"}</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-xl text-center border border-blue-100">
                      <p className="text-xs text-blue-600 font-semibold">Left Ear PTA</p>
                      <p className="text-3xl font-bold text-blue-700">{session.pta_left ?? "—"}<span className="text-sm ml-1">dB</span></p>
                      <p className={`text-sm font-semibold ${severityColor(session.severity_left)}`}>{session.severity_left || "—"}</p>
                    </div>
                  </div>
                  {aiRecommendation ? (
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-5 border border-indigo-100">
                      <h4 className="font-bold text-indigo-800 mb-2 flex items-center gap-2 text-sm"><Sparkles className="w-4 h-4" />AI Recommendation</h4>
                      <p className="text-gray-700 text-sm whitespace-pre-wrap">{aiRecommendation}</p>
                      {suggestedAids.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-indigo-100 flex flex-wrap gap-2">
                          {suggestedAids.map((a) => <Badge key={a.id} variant="outline" className="bg-white text-xs">{a.brand} {a.name}</Badge>)}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center">
                      <Button onClick={generateAI} disabled={generatingRec} className="bg-gradient-to-r from-indigo-600 to-purple-600">
                        {generatingRec ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                        {generatingRec ? "Generating..." : "Generate AI Recommendation"}
                      </Button>
                    </div>
                  )}
                  <div className="flex justify-center gap-3 pt-3 border-t">
                    <Button variant="outline" onClick={downloadReport} disabled={downloadingPdf} data-testid="download-pdf-btn">
                      {downloadingPdf ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                      Download PDF
                    </Button>
                    {session.patient_id && (
                      <Button variant="outline" onClick={() => navigate(`/patients/${session.patient_id}`)}>
                        <ArrowRight className="w-4 h-4 mr-2" /> Patient Profile
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ═══ Live Audiogram ═══ */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-teal-600" /> Live Audiogram</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative bg-white border rounded-xl p-4" style={{ height: "320px" }}>
              <div className="absolute left-0 top-0 bottom-0 w-10 flex flex-col justify-between py-4 text-[10px] text-gray-400">
                {[-10, 0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110].map((db) => <span key={db} className="text-right pr-1">{db}</span>)}
              </div>
              <div className="ml-10 h-full relative">
                <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
                  {[0,1,2,3,4,5,6,7,8,9,10,11,12].map((i) => <line key={`h${i}`} x1="0" y1={`${(i/12)*100}%`} x2="100%" y2={`${(i/12)*100}%`} stroke="#f3f4f6" strokeWidth={1} />)}
                  {FREQUENCIES.map((_,i) => <line key={`v${i}`} x1={`${(i/(FREQUENCIES.length-1))*100}%`} y1="0" x2={`${(i/(FREQUENCIES.length-1))*100}%`} y2="100%" stroke="#f3f4f6" strokeWidth={1} />)}
                  <line x1="0" y1={`${(30/120)*100}%`} x2="100%" y2={`${(30/120)*100}%`} stroke="#22c55e" strokeWidth={1.5} strokeDasharray="6,4" opacity="0.5" />
                  {/* Right ear — O markers */}
                  {(() => {
                    const pts = FREQUENCIES.map((f) => { const t = thresholds.right?.[f]; if(t===undefined) return null; return {x:(FREQUENCIES.indexOf(f)/(FREQUENCIES.length-1))*100, y:((t+10)/120)*100}; }).filter(Boolean);
                    return <>{pts.length > 1 && <polyline points={pts.map(p=>`${p.x}%,${p.y}%`).join(" ")} fill="none" stroke="#ef4444" strokeWidth="2"/>}{pts.map((p,i)=><circle key={`r${i}`} cx={`${p.x}%`} cy={`${p.y}%`} r="7" fill="none" stroke="#ef4444" strokeWidth="2.5"/>)}</>;
                  })()}
                  {/* Left ear — X markers */}
                  {(() => {
                    const pts = FREQUENCIES.map((f) => { const t = thresholds.left?.[f]; if(t===undefined) return null; return {x:(FREQUENCIES.indexOf(f)/(FREQUENCIES.length-1))*100, y:((t+10)/120)*100}; }).filter(Boolean);
                    return <>{pts.length > 1 && <polyline points={pts.map(p=>`${p.x}%,${p.y}%`).join(" ")} fill="none" stroke="#3b82f6" strokeWidth="2"/>}{pts.map((p,i)=>(<g key={`l${i}`}><line x1={`${p.x-1.2}%`} y1={`${p.y-1.8}%`} x2={`${p.x+1.2}%`} y2={`${p.y+1.8}%`} stroke="#3b82f6" strokeWidth="2.5"/><line x1={`${p.x-1.2}%`} y1={`${p.y+1.8}%`} x2={`${p.x+1.2}%`} y2={`${p.y-1.8}%`} stroke="#3b82f6" strokeWidth="2.5"/></g>))}</>;
                  })()}
                </svg>
                <div className="absolute -bottom-5 left-0 right-0 flex justify-between text-[10px] text-gray-400">
                  {FREQUENCIES.map((f) => <span key={f}>{fmtFreq(f)}</span>)}
                </div>
              </div>
              <div className="absolute top-2 right-2 flex gap-3 text-xs">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full border-2 border-red-500"/>Right (O)</span>
                <span className="flex items-center gap-1"><span className="text-blue-500 font-bold text-sm">X</span>Left (X)</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              {["right","left"].map((ear) => (
                <div key={ear}>
                  <h4 className={`text-xs font-bold mb-1.5 ${ear==="right"?"text-red-600":"text-blue-600"}`}>{ear==="right"?"Right":"Left"} Ear</h4>
                  <div className="flex flex-wrap gap-1">
                    {FREQUENCIES.map((f) => <span key={f} className={`text-[11px] px-2 py-0.5 rounded-md border ${thresholds[ear]?.[f]!==undefined?"bg-white border-gray-300 font-semibold":"bg-gray-50 border-gray-100 text-gray-400"}`}>{fmtFreq(f)}: {thresholds[ear]?.[f]??"—"}</span>)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
